import React from 'react';
import {
	Paper,
	List,
	ListItem,
	ListItemAvatar,
	Avatar,
	ListItemText,
	Typography,
	Divider,
	Stack,
	Chip,
	Box
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import LoginIcon from '@mui/icons-material/Login';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { formatDistanceToNow, format } from 'date-fns';

const opIcon = type => {
	switch (type) {
		case 'insert':
			return <AddCircleOutlineIcon fontSize="small" />;
		case 'update':
			return <EditIcon fontSize="small" />;
		case 'replace':
			return <AutorenewIcon fontSize="small" />;
		case 'delete':
			return <DeleteOutlineIcon fontSize="small" />;
		case 'login':
			return <LoginIcon fontSize="small" />;
		default:
			return <PersonOutlineIcon fontSize="small" />;
	}
};

export function AuditLogTimeline({ logs = [] }) {
	const items = React.useMemo(() => logs.slice(0, 12), [logs]);

	return (
		<Paper
			variant="outlined"
			sx={{
				mt: 2,
				borderRadius: 3,
				p: 2,
				bgcolor: 'background.paper',
				boxShadow: '0 8px 30px rgba(0,0,0,0.04)',
				maxHeight: 300,
				height: 300,
				overflowY: 'auto'
			}}
		>
			<Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
				Recent Activity
			</Typography>
			<List disablePadding>
				{items.map((item, index) => {
					const timestamp = item?.timestamp ? new Date(item.timestamp) : null;
					const relative = timestamp && !Number.isNaN(timestamp.getTime()) ? formatDistanceToNow(timestamp, { addSuffix: true }) : 'N/A';
					const absolute = timestamp && !Number.isNaN(timestamp.getTime()) ? format(timestamp, 'PP pp') : '';
					return (
						<React.Fragment key={item?._id || index}>
							<ListItem alignItems="flex-start" sx={{ px: 0 }}>
								<ListItemAvatar>
									<Avatar variant="rounded" sx={{ bgcolor: 'grey.100', color: 'text.primary', width: 40, height: 40 }}>
										{opIcon(item?.operationType)}
									</Avatar>
								</ListItemAvatar>
								<ListItemText
									primary={
										<Stack direction="row" spacing={1} alignItems="center">
											<Typography variant="body1" fontWeight={700}>
												{item?.eventAction || 'Unknown event'}
											</Typography>
											<Chip
												size="small"
												label={(item?.operationType || '').toUpperCase()}
												color={item?.operationType === 'delete' ? 'error' : 'default'}
											/>
											<Chip size="small" variant="outlined" label={item?.collection || 'N/A'} />
										</Stack>
									}
									secondary={
										<Stack spacing={0.5}>
											<Typography variant="body2" color="text.secondary">
												{item?.updatedBy || 'Unknown actor'} - {item?.actorType || 'unknown'}
											</Typography>
											<Typography variant="caption" color="text.secondary">
												{relative} {absolute && `- ${absolute}`}
											</Typography>
										</Stack>
									}
								/>
							</ListItem>
							{index < items.length - 1 && (
								<Box sx={{ pl: { xs: 6, sm: 7 }, pr: 1 }}>
									<Divider />
								</Box>
							)}
						</React.Fragment>
					);
				})}
				{!items.length && (
					<Box sx={{ py: 2, textAlign: 'center' }}>
						<Typography variant="body2" color="text.secondary">
							No recent activity yet.
						</Typography>
					</Box>
				)}
			</List>
		</Paper>
	);
}
